import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import { countryCodeAlpha2Lookup } from "@entrilia/shared/utilities/countriesProvider";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import {
  bicCodeValidator,
  combineValidators,
  ibanValidator,
  invalidResult,
  maxCharactersValidator,
  minCharactersValidator,
  regexValidator,
  requiredValidator,
  routingNumberValidator,
  swiftCompliantCharsValidator,
  validResult,
} from "@entrilia/shared/utilities/validators";
import type { CreateInvestorBankAccountRequest } from "../../../../../api/types/bankAccountTypes";
import type { ChangeRequestAttachment } from "../../../../../api/types/changeRequestsTypes";
import type { InvestorFundPermissionSet } from "../../../../../store/state/user/types";

export interface NewBankAccountState {
  step: StateStep;
  isNextStepDisabled: boolean;
  bankAccountForm: BankAccountForm;
  fundAssignments: NewBankAccountFundAssignment[];
  comment: string;
  attachments: ChangeRequestAttachment[];
}

export enum StateStep {
  BankAccount = 0,
  FundAssignments = 1,
  Review = 2,
}

interface BankAccountForm {
  addForFurtherCredit: boolean;
  countryCode: string | undefined;
  values: BankAccountFormValues;
  touchedFields: (keyof BankAccountFormValues)[];
  errors: BankAccountFormErrors;
}

interface BankAccountFormErrors {
  country?: string;
  bankName?: string;
  abaNumber?: string;
  swiftCode?: string;
  accountName?: string;
  iban?: string;
  accountNumber?: string;
  ibanAndAccountNumber?: string;
  currencyCode?: string;
  ffcName?: string;
  ffcAccountNumber?: string;
  institutionNumber?: string;
  transitNumber?: string;
  bankAddress?: string;
  bankAddress2?: string;
  bankCity?: string;
  bankState?: string;
  bankZipCode?: string;
}

interface BankAccountFormValues {
  country: string;
  bankName: string;
  abaNumber: string;
  swiftCode: string;
  accountName: string;
  iban: string;
  accountNumber: string;
  currencyCode: string;
  ffcName: string;
  ffcAccountNumber: string;
  institutionNumber: string;
  transitNumber: string;
  bankAddress: string;
  bankAddress2: string;
  bankCity: string;
  bankState: string;
  bankZipCode: string;
}

export interface BankAccountFormLayout {
  showAbaNumber: boolean;
  showIban: boolean;
  isSwiftRequired: boolean;
  showBankAddress: boolean;
  showInstitutionAndTransitNumbers: boolean;
}

export interface NewBankAccountFundAssignment {
  fundInvestorId: string;
  fundName: string;
  selected: boolean;
  disabled: boolean;
}

export type Action =
  | {
      type: "next_step";
    }
  | {
      type: "previous_step";
    }
  | {
      type: "update_field";
      field: keyof BankAccountFormValues;
      value: string;
    }
  | {
      type: "update_addForFurtherCredit";
      value: boolean;
    }
  | {
      type: "toggle_fund_assignment";
      fundInvestorId: string;
    }
  | {
      type: "update_comment";
      value: string;
    }
  | {
      type: "update_attachments";
      value: ChangeRequestAttachment[];
    };

export const getInitialState = (
  fundInvestors: InvestorFundPermissionSet[],
  availableFundInvestorIds: string[],
  investorName: string
): NewBankAccountState => ({
  step: StateStep.BankAccount,
  isNextStepDisabled: true,
  bankAccountForm: {
    addForFurtherCredit: false,
    countryCode: undefined,
    values: {
      country: "",
      bankName: "",
      abaNumber: "",
      swiftCode: "",
      iban: "",
      accountName: investorName,
      accountNumber: "",
      currencyCode: "",
      ffcName: "",
      ffcAccountNumber: "",
      institutionNumber: "",
      transitNumber: "",
      bankAddress: "",
      bankAddress2: "",
      bankCity: "",
      bankState: "",
      bankZipCode: "",
    },
    touchedFields: investorName ? ["accountName"] : [],
    errors: {},
  },
  fundAssignments: fundInvestors.map((fundInvestor) => ({
    fundInvestorId: fundInvestor.fundInvestorId,
    fundName: fundInvestor.fundName,
    selected: false,
    disabled: !availableFundInvestorIds.includes(fundInvestor.fundInvestorId),
  })),
  comment: "",
  attachments: [],
});

const ibanAndAccountNumberValidator = (iban: string, accountNumber: string): ValidationResult => {
  if (!iban && !accountNumber) {
    return invalidResult("IBAN or account number is required");
  }
  return validResult();
};

const validateFormForUsa = (formFields: BankAccountFormValues, addForFurtherCredit: boolean): BankAccountFormErrors => {
  return {
    country: requiredValidator(formFields.country).error,
    bankName: combineValidators(requiredValidator, maxCharactersValidator(100))(formFields.bankName).error,
    abaNumber: combineValidators(requiredValidator, routingNumberValidator)(formFields.abaNumber).error,
    swiftCode: bicCodeValidator(formFields.swiftCode).error,
    accountName: combineValidators(requiredValidator, maxCharactersValidator(250))(formFields.accountName).error,
    iban: undefined,
    accountNumber: combineValidators(
      requiredValidator,
      minCharactersValidator(4),
      maxCharactersValidator(30)
    )(formFields.accountNumber).error,
    ibanAndAccountNumber: undefined,
    currencyCode: requiredValidator(formFields.currencyCode).error,
    ffcName: addForFurtherCredit
      ? combineValidators(requiredValidator, maxCharactersValidator(100))(formFields.ffcName).error
      : undefined,
    ffcAccountNumber: addForFurtherCredit
      ? combineValidators(requiredValidator, maxCharactersValidator(30))(formFields.ffcAccountNumber).error
      : undefined,
  };
};

const validateFormForNonUsa = (
  formFields: BankAccountFormValues,
  addForFurtherCredit: boolean
): BankAccountFormErrors => {
  return {
    country: requiredValidator(formFields.country).error,
    bankName: combineValidators(requiredValidator, maxCharactersValidator(100))(formFields.bankName).error,
    abaNumber: undefined,
    swiftCode: combineValidators(requiredValidator, bicCodeValidator)(formFields.swiftCode).error,
    accountName: combineValidators(
      requiredValidator,
      swiftCompliantCharsValidator,
      maxCharactersValidator(250)
    )(formFields.accountName).error,
    iban: ibanValidator(formFields.iban).error,
    accountNumber: combineValidators(
      swiftCompliantCharsValidator,
      minCharactersValidator(4),
      maxCharactersValidator(30)
    )(formFields.accountNumber).error,
    ibanAndAccountNumber: ibanAndAccountNumberValidator(formFields.iban, formFields.accountNumber).error,
    currencyCode: requiredValidator(formFields.currencyCode).error,
    ffcName: addForFurtherCredit
      ? combineValidators(
          requiredValidator,
          swiftCompliantCharsValidator,
          maxCharactersValidator(100)
        )(formFields.ffcName).error
      : undefined,
    ffcAccountNumber: addForFurtherCredit
      ? combineValidators(
          requiredValidator,
          swiftCompliantCharsValidator,
          maxCharactersValidator(30)
        )(formFields.ffcAccountNumber).error
      : undefined,
  };
};

const validateFormForCanada = (
  formFields: BankAccountFormValues,
  addForFurtherCredit: boolean
): BankAccountFormErrors => ({
  ...validateFormForNonUsa(formFields, addForFurtherCredit),
  iban: undefined,
  accountNumber: combineValidators(
    requiredValidator,
    swiftCompliantCharsValidator,
    minCharactersValidator(4),
    maxCharactersValidator(30)
  )(formFields.accountNumber).error,
  ibanAndAccountNumber: undefined,
  institutionNumber: combineValidators(
    requiredValidator,
    regexValidator(/^\d{3}$/, "Institution number must be 3 digits")
  )(formFields.institutionNumber).error,
  transitNumber: combineValidators(
    requiredValidator,
    regexValidator(/^\d{5}$/, "Transit number must be 5 digits")
  )(formFields.transitNumber).error,
  bankAddress: combineValidators(requiredValidator, maxCharactersValidator(100))(formFields.bankAddress).error,
  bankAddress2: maxCharactersValidator(50)(formFields.bankAddress2).error,
  bankCity: combineValidators(requiredValidator, maxCharactersValidator(30))(formFields.bankCity).error,
  bankState: combineValidators(requiredValidator, maxCharactersValidator(30))(formFields.bankState).error,
  bankZipCode: combineValidators(requiredValidator, maxCharactersValidator(20))(formFields.bankZipCode).error,
});

const validateForm = (
  formFields: BankAccountFormValues,
  addForFurtherCredit: boolean,
  countryCode: string | undefined
): BankAccountFormErrors => {
  switch (countryCode) {
    case "US":
      return validateFormForUsa(formFields, addForFurtherCredit);
    case "CA":
      return validateFormForCanada(formFields, addForFurtherCredit);
    default:
      return validateFormForNonUsa(formFields, addForFurtherCredit);
  }
};

export const getBankAccountFormLayout = (countryCode: string | undefined): BankAccountFormLayout => ({
  showAbaNumber: countryCode === "US",
  showIban: countryCode !== "US" && countryCode !== "CA",
  isSwiftRequired: countryCode !== "US",
  showBankAddress: countryCode === "CA",
  showInstitutionAndTransitNumbers: countryCode === "CA",
});

const trimmedOrUndefined = (value: string): string | undefined => value.trim() || undefined;

export const stateToCreateInvestorBankAccountRequest = (
  state: NewBankAccountState
): CreateInvestorBankAccountRequest => {
  const { values, countryCode, addForFurtherCredit } = state.bankAccountForm;
  const isCanada = countryCode === "CA";

  return {
    fundInvestorIds: state.fundAssignments
      .filter((assignment) => assignment.selected)
      .map((assignment) => assignment.fundInvestorId),
    bankName: values.bankName.trim(),
    country: values.country,
    currencyCode: values.currencyCode,
    accountName: values.accountName.trim(),
    iban: isCanada ? undefined : trimmedOrUndefined(values.iban),
    accountNumber: trimmedOrUndefined(values.accountNumber),
    swiftCode: trimmedOrUndefined(values.swiftCode),
    abaNumber: countryCode === "US" ? trimmedOrUndefined(values.abaNumber) : undefined,
    ffcName: addForFurtherCredit ? trimmedOrUndefined(values.ffcName) : undefined,
    ffcAccountNumber: addForFurtherCredit ? trimmedOrUndefined(values.ffcAccountNumber) : undefined,
    institutionNumber: isCanada ? trimmedOrUndefined(values.institutionNumber) : undefined,
    transitNumber: isCanada ? trimmedOrUndefined(values.transitNumber) : undefined,
    address: isCanada ? trimmedOrUndefined(values.bankAddress) : undefined,
    address2: isCanada ? trimmedOrUndefined(values.bankAddress2) : undefined,
    city: isCanada ? trimmedOrUndefined(values.bankCity) : undefined,
    state: isCanada ? trimmedOrUndefined(values.bankState) : undefined,
    zipCode: isCanada ? trimmedOrUndefined(values.bankZipCode) : undefined,
    commentUserInput: trimmedOrUndefined(state.comment),
    attachmentDataCatalogIds:
      state.attachments.length > 0 ? state.attachments.map((attachment) => attachment.dataCatalogId) : undefined,
  };
};

export const reducer = (state: NewBankAccountState, action: Action): NewBankAccountState => {
  switch (action.type) {
    case "next_step": {
      const nextStep = (state.step + 1) as StateStep;

      const isNextStepDisabled =
        nextStep === StateStep.FundAssignments
          ? !state.fundAssignments.some((assignment) => assignment.selected)
          : false;

      return {
        ...state,
        isNextStepDisabled,
        step: nextStep,
      };
    }
    case "previous_step":
      return {
        ...state,
        isNextStepDisabled: false,
        step: state.step - 1,
      };
    case "update_field": {
      const countryCode =
        action.field === "country" ? countryCodeAlpha2Lookup.get(action.value) : state.bankAccountForm.countryCode;
      const updatedFields: BankAccountFormValues = { ...state.bankAccountForm.values, [action.field]: action.value };
      const allErrors = validateForm(updatedFields, state.bankAccountForm.addForFurtherCredit, countryCode);
      const isFormValid = Object.values(allErrors).every((error) => !error);
      const touchedFields = distinct([...state.bankAccountForm.touchedFields, action.field]);
      const visibleErrors = Object.fromEntries(
        Object.entries(allErrors).filter(([key]) => touchedFields.includes(key as keyof BankAccountFormValues))
      );

      return {
        ...state,
        isNextStepDisabled: !isFormValid,
        bankAccountForm: {
          ...state.bankAccountForm,
          countryCode: countryCode,
          values: updatedFields,
          touchedFields,
          errors: visibleErrors,
        },
      };
    }
    case "update_addForFurtherCredit": {
      const allErrors = validateForm(state.bankAccountForm.values, action.value, state.bankAccountForm.countryCode);
      const isFormValid = Object.values(allErrors).every((error) => !error);
      const visibleErrors = Object.fromEntries(
        Object.entries(allErrors).filter(([key]) =>
          state.bankAccountForm.touchedFields.includes(key as keyof BankAccountFormValues)
        )
      );

      return {
        ...state,
        isNextStepDisabled: !isFormValid,
        bankAccountForm: {
          ...state.bankAccountForm,
          addForFurtherCredit: action.value,
          errors: visibleErrors,
        },
      };
    }
    case "toggle_fund_assignment": {
      const newFundAssignments = state.fundAssignments.map((assignment) =>
        assignment.fundInvestorId === action.fundInvestorId
          ? { ...assignment, selected: !assignment.selected }
          : assignment
      );

      return {
        ...state,
        isNextStepDisabled: !newFundAssignments.some((assignment) => assignment.selected),
        fundAssignments: newFundAssignments,
      };
    }
    case "update_comment":
      return {
        ...state,
        comment: action.value,
      };
    case "update_attachments":
      return {
        ...state,
        attachments: action.value,
      };
    default:
      return state;
  }
};
